import { useState, useCallback, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import AnnouncementCategoryCard from '../../shared/announcementCategoryCard';
import {
  ANNOUNCEMENT_CATEGORY_KEYS,
  ANNOUNCEMENT_CATEGORY_TYPES,
} from '../../../constants/announcements';
import { API_ANNOUNCEMENTS_CATEGORIES } from '../../../constants/apiRoutes';
import { APP_PAGES } from '../../../constants/pages';
import { sticky } from '../../../constants/helperCssRules';
import { useTranslations } from '../../../utility/useTranslations';
import http from '../../../utility/http';
import { prepareAnnouncementCategoryPath } from '../../../utility/announcements';
import { getItemById } from '../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  header: {
    ...sticky(primary.white, 105),
    padding: spacing(8, 0, 6, 0),
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gridGap: spacing(6),
    alignItems: 'start',
  },
}));

const AnnouncementsPage = ({ navigate, ...rest }) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.ANNOUNCEMENTS);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [announcementCategories, setAnnouncementCategories] = useState([]);

  const onGoToCategoryDetails = useCallback(
    categoryKey => () => navigate(prepareAnnouncementCategoryPath(categoryKey)),
    [navigate]
  );

  const getInitialData = useCallback(async () => {
    try {
      const { data } = await http.get(API_ANNOUNCEMENTS_CATEGORIES);
      setAnnouncementCategories(data);
    } finally {
      setIsInitialLoad(false);
    }
  }, []);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  return (
    <PageContainer
      {...rest}
      navigate={navigate}
      translations={translations}
      shouldPassProps={false}
      isMediumWidthContent
    >
      {!isInitialLoad && (
        <div>
          <div className={classes.header}>
            <Typography variant="body2">{translations.description}</Typography>
          </div>
          <div className={classes.grid}>
            {ANNOUNCEMENT_CATEGORY_KEYS.map(categoryKey => {
              const category = getItemById(
                announcementCategories,
                categoryKey,
                'category'
              );

              return (
                <AnnouncementCategoryCard
                  key={categoryKey}
                  translations={translations.categories[categoryKey]}
                  category={ANNOUNCEMENT_CATEGORY_TYPES[categoryKey]}
                  announcements={category?.announcements ?? []}
                  totalCount={category?.total ?? 0}
                  onGoToCategoryDetails={onGoToCategoryDetails(categoryKey)}
                />
              );
            })}
          </div>
        </div>
      )}
    </PageContainer>
  );
};

AnnouncementsPage.propTypes = {
  navigate: PropTypes.func.isRequired,
};

export default memo(AnnouncementsPage);
